import { marketSearchApi } from '@envato/foundation-types'
import * as classnames from 'classnames'
import omit from 'lodash-es/omit'
import * as React from 'react'

import { ILocationParams } from '../../../../common/reducers/reactRouterLocation'
import { commaStyleString } from '../../../../common/utils/formatString'
import ChevronLeftIcon from '../../../components/icons/chevronLeft'
import SearchLink from '../../../containers/searchLinkContainer'
import * as styles from './filterCategory.module.css'

interface IProps {
  type: string
  items: marketSearchApi.IFilterOption[]
  locationParams: ILocationParams
  separator?: string
  scrollable?: boolean
}

const isImmediateChildCategory = (
  currentCategory: string,
  potentialChildCategory: string
): boolean => {
  const currentDepth = currentCategory.split('/').length
  const potentialChildDepth = potentialChildCategory.split('/').length
  const currentIsRoot = currentCategory === ''
  const potentialChildCategoryIs1LevelDeeper = currentIsRoot
    ? potentialChildDepth === 1
    : currentDepth === potentialChildDepth - 1

  return (
    potentialChildCategory.startsWith(currentCategory) &&
    potentialChildCategoryIs1LevelDeeper
  )
}

const isImmediateChildPlatform = (
  currentPlatform: string,
  potentialChildPlatform: string
): boolean => {
  return potentialChildPlatform.startsWith(currentPlatform)
}

const isImmediateChild = (
  currentKey: string,
  potentialChildKey: string,
  type: string
): boolean => {
  return type === 'category'
    ? isImmediateChildCategory(currentKey, potentialChildKey)
    : isImmediateChildPlatform(currentKey, potentialChildKey)
}

const FilterCategory = ({
  type,
  items,
  locationParams,
  separator = '/',
  scrollable = false
}: IProps) => {
  const currentCategory = locationParams[type] || ''
  const parentCategories = currentCategory
    .split(separator)
    .slice(0, -1)
    .map((category: string, index: number, path: string[]) => {
      return path.slice(0, index + 1).join(separator)
    })
  const visibleItems = items.filter(
    item =>
      item.key === '' ||
      parentCategories.includes(item.key) ||
      item.key === currentCategory ||
      isImmediateChild(currentCategory, item.key, type)
  )

  const rootClassNames = classnames(styles.root, {
    [styles.rootScrollable]: scrollable
  })

  return (
    <nav className={rootClassNames}>
      <ul data-test-selector="category-filter">
        {visibleItems.map(item => {
          const newQuery = omit(locationParams, ['page'])
          newQuery[type] = item.key

          const isParentCategory = !item.key.startsWith(currentCategory)
          const isCurrentCategory = item.key === currentCategory
          const isChildCategory = !isParentCategory && !isCurrentCategory

          const classNames = classnames(styles.itemLink, {
            [styles.itemRootLink]: item.key === '',
            [styles.itemParentLink]: isParentCategory,
            [styles.itemCurrentLink]: isCurrentCategory,
            [styles.itemChildLink]: isChildCategory
          })

          const isRootLevelCategory = newQuery.category.split('/').length === 1

          const excludeFilesTypes =
            (isRootLevelCategory && !isParentCategory) || item.key === ''

          // eslint-disable-next-line
          const { files_included, ...queryWithoutFilesTypes } = newQuery

          return (
            <li key={item.key} className={styles.item}>
              <SearchLink
                locationParams={
                  excludeFilesTypes ? queryWithoutFilesTypes : newQuery
                }
                hash="content"
                className={classNames}
              >
                {isParentCategory && (
                  <ChevronLeftIcon className={styles.chevronIcon} />
                )}
                {item.name || item.label}
              </SearchLink>
              <span className={styles.count}>
                {commaStyleString(item.count, ',')}
              </span>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

export default FilterCategory
